/**
 * Service wrapper for Google Identity Services (GIS) OAuth2 flow.
 * Handles token acquisition, storage, and expiration for Google Calendar API access.
 */
import { MS_PER_MINUTE } from '@/config/constants';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const SCOPES =
  'https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/calendar.events';
const TOKEN_KEY = 'gcal_oauth_token';

class GCalAuthService {
  constructor() {
    this.tokenClient = null;
    this._initialized = false;
  }

  /**
   * Initializes the GIS token client.
   * Should be called after the Google Identity Services script is loaded.
   */
  init() {
    if (this._initialized || !window.google) return;

    this.tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: SCOPES,
      callback: (response) => {
        if (response.error) {
          console.error('GIS Error:', response.error);
          return;
        }
        this._saveToken(response);
      },
    });
    this._initialized = true;
  }

  /**
   * Requests a new token or refreshes the current one.
   * @param {boolean} prompt - Whether to force a consent prompt.
   * @returns {Promise<string>}
   */
  async getToken(prompt = false) {
    const stored = this._getStoredToken();

    if (stored && !this._isExpired(stored) && !prompt) {
      return stored.access_token;
    }

    return new Promise((resolve, reject) => {
      if (!this.tokenClient) {
        this.init();
      }

      if (!this.tokenClient) {
        console.error(
          '[GCalAuthService] Failed to initialize token client (GIS script might not be loaded)',
        );
        reject(new Error('Google Identity Services not initialized'));
        return;
      }

      // Overwrite callback for this specific request to resolve the promise
      const originalCallback = this.tokenClient.callback;
      this.tokenClient.callback = (response) => {
        if (response.error) {
          reject(new Error(response.error));
        } else {
          this._saveToken(response);
          resolve(response.access_token);
        }
        this.tokenClient.callback = originalCallback;
      };

      this.tokenClient.requestAccessToken({ prompt: prompt ? 'consent' : '' });
    });
  }

  /**
   * Clears the stored token.
   */
  logout() {
    const token = this._getStoredToken();
    if (token && window.google) {
      window.google.accounts.oauth2.revoke(token.access_token, () => {
        localStorage.removeItem(TOKEN_KEY);
      });
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  }

  _saveToken(response) {
    const tokenData = {
      ...response,
      expires_at: Date.now() + response.expires_in * 1000 - MS_PER_MINUTE, // Buffer of 1 minute
    };
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokenData));
  }

  _getStoredToken() {
    const data = localStorage.getItem(TOKEN_KEY);
    return data ? JSON.parse(data) : null;
  }

  _isExpired(token) {
    return Date.now() >= token.expires_at;
  }

  get isAuthenticated() {
    const token = this._getStoredToken();
    return !!token && !this._isExpired(token);
  }

  /**
   * Resets the internal state (for testing purposes).
   */
  reset() {
    this.tokenClient = null;
    this._initialized = false;
  }
}

export const gcalAuthService = new GCalAuthService();
